import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, gt, eq, not } from 'ember-truth-helpers';
import { join } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsButton,
  HdsApplicationState,
  HdsButtonSet,
  HdsLinkStandalone,
  HdsAlert,
  HdsTable,
  HdsBadge,
  HdsTooltipButton,
  HdsIcon,
  HdsDropdown,
  HdsModal,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  ModalConfirm,
} from 'core/components';
import { optionForSourceState } from 'core/helpers';
import { boxMargin, boxPadding } from 'core/modifiers';

import type ApiService from 'api/services/api';
import type OperationService from 'core/services/operation';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import type {
  HashicorpCloudNetwork20200907DNSForwarding as DNSForwarding,
  HashicorpCloudNetwork20200907DNSForwardingRule as DNSForwardingRule,
  HashicorpCloudLocationLink as LocationLink,
} from '@clients/cloud-network';
import type {
  HashicorpCloudResourcemanagerOrganization as Organization,
  HashicorpCloudResourcemanagerProject as Project,
} from '@clients/cloud-resource-manager';
import type { CoreLayoutMainSignature } from 'core/components/core/layout/main';

interface PageHvnDnsForwardingCreateSignature {
  Args: {
    model: {
      dnsForwardings: DNSForwarding[];
      dnsForwardingRules: DNSForwardingRule[];
      network: LocationLink;
      organization: Organization;
      project: Project;
    };
  };

  Element: CoreLayoutMainSignature['Element'];
}

const MAXIMUM_DNS_FORWARDING_RULES = 5;

export default class PageHvnDnsForwardingCreate extends Component<PageHvnDnsForwardingCreateSignature> {
  @service declare readonly api: ApiService;
  @service declare readonly flashMessages: FlashMessagesService;
  @service declare readonly intl: IntlService;
  @service declare readonly operation: OperationService;

  @tracked activeRuleToDelete: DNSForwardingRule | null = null;
  @tracked allowDelete = false;

  openDeleteModal = (rule: DNSForwardingRule) => {
    this.activeRuleToDelete = rule;
  };

  onCloseDeleteModal = () => {
    this.activeRuleToDelete = null;
  };

  get hasMaximumNumberOfRules() {
    const rulesLength = this.args.model.dnsForwardingRules.length;
    return rulesLength >= MAXIMUM_DNS_FORWARDING_RULES;
  }

  deleteRule = task(async () => {
    if (!this.activeRuleToDelete) {
      return;
    }
    const { dnsForwardings, network, organization, project } = this.args.model;
    const dnsForwarding = dnsForwardings[0];
    const { operation } = await this.api.network.deleteDNSForwardingRule(
      organization.id!,
      project.id!,
      network.id!,
      dnsForwarding!.id!,
      this.activeRuleToDelete.rule!.id!
    );

    this.operation.addOperation(operation);

    this.flashMessages.success(
      this.intl.t(
        'components.page.dns-forwarding-rules.list.flash-message.delete',
        { domain: this.activeRuleToDelete.rule?.domainName }
      )
    );
    this.activeRuleToDelete = null;
  });

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Title data-test-dns-forwarding-title>
            {{t "components.page.dns-forwarding-rules.list.custom-rules.title"}}
          </PH.Title>
          {{#if
            (and
              (gt @model.dnsForwardingRules.length 0)
              (can
                "create network-dns-forwarding-rule" @model.dnsForwardingRules
              )
            )
          }}
            <PH.Actions>
              <HdsButton
                data-test-dns-forwarding-create
                @icon="plus"
                @route="cloud.orgs.detail.projects.detail.hvns.detail.dns-forwarding-rules.create"
                @text={{t
                  "components.page.dns-forwarding-rules.list.buttons.create"
                }}
              />
            </PH.Actions>
          {{/if}}
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
        </HdsPageHeader>
      </:header>
      <:body>
        {{#if (eq @model.dnsForwardingRules.length 0)}}
          <HdsApplicationState
            {{boxPadding "2xl 0"}}
            data-test-dns-forwarding-zero-state
            as |A|
          >
            <A.Header
              @title={{t
                "components.page.dns-forwarding-rules.list.empty.header"
              }}
            />
            <A.Body {{boxMargin "md 0 lg"}}>
              {{t "components.page.dns-forwarding-rules.list.empty.message"}}
            </A.Body>
            <A.Footer>
              <HdsButtonSet>
                {{#if
                  (can
                    "create network-dns-forwarding-rule"
                    @model.dnsForwardingRules
                  )
                }}
                  <HdsButton
                    data-test-dns-forwarding-create
                    @icon="plus"
                    @route="cloud.orgs.detail.projects.detail.hvns.detail.dns-forwarding-rules.create"
                    @text={{t
                      "components.page.dns-forwarding-rules.list.buttons.create"
                    }}
                  />
                {{/if}}
                <HdsLinkStandalone
                  @icon="docs-link"
                  @iconPosition="trailing"
                  @href={{t
                    "components.page.dns-forwarding-rules.list.empty.how-does-it-work.url"
                  }}
                  @size="small"
                  @text={{t
                    "components.page.dns-forwarding-rules.list.empty.how-does-it-work.text"
                  }}
                  data-test-dns-forwarding-how-link
                />
              </HdsButtonSet>
            </A.Footer>
          </HdsApplicationState>
        {{else}}
          {{#if this.hasMaximumNumberOfRules}}
            <HdsAlert
              @type="inline"
              {{boxMargin "0 0 md"}}
              data-test-dns-forwarding-max-rules
              as |A|
            >
              <A.Title>
                {{t
                  "components.page.dns-forwarding-rules.list.limit-met-alert.title"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.dns-forwarding-rules.list.limit-met-alert.description"
                }}
              </A.Description>
            </HdsAlert>
          {{/if}}
          <HdsTable data-test-dns-forwarding-rules-table>
            <:head as |H|>
              <H.Tr>
                <H.Th data-test-dns-forwarding-domain-header>
                  {{t
                    "components.page.dns-forwarding-rules.list.table.fields.forwarded-domain-name"
                  }}
                </H.Th>
                <H.Th data-test-dns-forwarding-ips-header>
                  {{t
                    "components.page.dns-forwarding-rules.list.table.fields.dns-name-server-ip"
                  }}
                </H.Th>
                <H.Th data-test-dns-forwarding-status-header>
                  {{t
                    "components.page.dns-forwarding-rules.list.table.fields.status"
                  }}
                </H.Th>
                <H.Th data-test-dns-forwarding-action-header>
                  {{t
                    "components.page.dns-forwarding-rules.list.table.fields.action"
                  }}
                </H.Th>
              </H.Tr>
            </:head>
            <:body as |B|>
              {{#each @model.dnsForwardingRules as |forwardingRule|}}
                {{#let forwardingRule.rule as |rule|}}
                  {{#let
                    (optionForSourceState
                      "dns-forwarding-rule" forwardingRule.state
                    )
                    as |option|
                  }}
                    <B.Tr
                      data-test-dns-forwarding-rules-table-row
                      id={{concat "row-" rule.id}}
                    >
                      <B.Td data-test-dns-forwarding-domain-field>
                        {{rule.domainName}}
                      </B.Td>
                      <B.Td data-test-dns-forwarding-ips-field>
                        {{join ", " rule.inboundEndpointIps}}
                      </B.Td>
                      <B.Td data-test-dns-forwarding-status-field>
                        <Flex @alignItems="center" @gap="2xs">
                          <HdsBadge
                            @color={{option.color}}
                            @text={{t option.text}}
                            @icon={{option.icon}}
                            @type="outlined"
                            data-test-dns-forwarding-status-badge
                          />
                          {{#if (eq forwardingRule.state "FAILED")}}
                            <HdsTooltipButton
                              @text={{t
                                "components.page.dns-forwarding-rules.list.table.tooltips.failed-more-info"
                              }}
                              aria-label={{t
                                "components.page.dns-forwarding-rules.list.table.buttons.more-info"
                              }}
                              data-test-dns-forwarding-failed-more-info-button
                            >
                              <HdsIcon @name="info" />
                            </HdsTooltipButton>
                          {{/if}}
                        </Flex>
                      </B.Td>
                      <B.Td data-test-dns-forwarding-actions-field>

                        {{#if
                          (and
                            (can
                              "delete network-dns-forwarding-rule"
                              forwardingRule
                            )
                            (not this.deleteRule.isRunning)
                          )
                        }}
                          <HdsDropdown @listPosition="bottom-left" as |D|>
                            <D.ToggleIcon
                              @color="secondary"
                              @hasChevron={{false}}
                              @icon="more-horizontal"
                              @size="small"
                              @text={{t
                                "components.page.dns-forwarding-rules.list.table.buttons.actions"
                              }}
                              data-test-dns-forwarding-action-button
                            />
                            <D.Interactive
                              @icon="trash"
                              @color="critical"
                              @text={{t
                                "components.page.dns-forwarding-rules.list.table.actions.delete"
                              }}
                              {{on
                                "click"
                                (fn this.openDeleteModal forwardingRule)
                              }}
                              data-test-dns-forwarding-delete-button
                            />
                          </HdsDropdown>
                        {{/if}}
                      </B.Td>
                    </B.Tr>
                  {{/let}}
                {{/let}}
              {{/each}}
            </:body>
          </HdsTable>
        {{/if}}
        {{#if this.activeRuleToDelete}}
          <HdsModal
            @color="critical"
            @onClose={{this.onCloseDeleteModal}}
            @returnFocusTo={{concat "rule-" this.activeRuleToDelete.rule.id}}
            as |M|
          >
            <M.Header @icon="alert-diamond">
              {{t "components.page.dns-forwarding-rules.list.modal.header"}}
            </M.Header>
            <M.Body>
              <p
                class="hds-typography-body-200 hds-foreground-primary"
                {{boxMargin "0 0 md"}}
              >
                {{t
                  "components.page.dns-forwarding-rules.list.modal.body"
                  htmlSafe=true
                  domain=this.activeRuleToDelete.rule.domainName
                }}
              </p>
              <ModalConfirm @onAllowedChange={{set this "allowDelete"}} />
            </M.Body>
            <M.Footer as |F|>
              <HdsButtonSet>
                <HdsButton
                  @color="critical"
                  @text={{t
                    "components.page.dns-forwarding-rules.list.modal.buttons.delete"
                  }}
                  {{on "click" this.deleteRule.perform}}
                  data-test-dns-forwarding-confirm-delete
                  disabled={{not this.allowDelete}}
                />
                <HdsButton
                  @color="secondary"
                  @text={{t
                    "components.page.dns-forwarding-rules.list.modal.buttons.cancel"
                  }}
                  {{on "click" F.close}}
                />
              </HdsButtonSet>
            </M.Footer>
          </HdsModal>
        {{/if}}
      </:body>
    </CoreLayoutMain>
  </template>
}
